import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { ImageThumbnail } from "../../components/upload/ImageUpload.jsx";

const STATUS_TONE = {
  open: "success",
  for_sale_lease: "warning",
  ex_display: "default",
};

export default function DisplayHomesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Display Homes · Admin";
  }, []);

  const { list, remove } = useResource("display-homes", { toastLabel: "display home" });
  const { data, isLoading } = list({
    page,
    limit: 12,
    sort: "sortOrder:asc",
    ...(search && { search }),
    ...(status && { status }),
  });

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Catalogue</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Display Homes</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} display home{data?.meta?.total === 1 ? "" : "s"} total
          </p>
        </div>
        <Link to="/display-homes/new">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New display home</Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name…"
          filters={
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="min-w-[160px]"
            >
              <option value="">Any status</option>
              <option value="open">Open</option>
              <option value="for_sale_lease">For sale / lease</option>
              <option value="ex_display">Ex-display</option>
            </Select>
          }
        />

        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          onRowClick={(row) => navigate(`/display-homes/${row._id}`)}
          columns={[
            {
              key: "name",
              label: "Name",
              render: (r) => (
                <div className="flex items-center gap-3">
                  <ImageThumbnail src={r.images?.[0]} alt={r.name} />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text-primary">{r.name}</p>
                    <p className="truncate text-xs text-text-muted">{r.address || "—"}</p>
                  </div>
                </div>
              ),
            },
            {
              key: "locationGroup",
              label: "Location",
              render: (r) => r.locationGroup ?? "—",
            },
            {
              key: "linkedDesign",
              label: "Design",
              render: (r) => (
                <span className="text-text-secondary">{r.linkedDesign?.name ?? "—"}</span>
              ),
            },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <Badge tone={STATUS_TONE[r.status] ?? "default"}>
                  {r.status?.replace(/_/g, " ")}
                </Badge>
              ),
            },
            {
              key: "isHidden",
              label: "Visibility",
              render: (r) =>
                r.isHidden ? (
                  <Badge tone="default">Hidden</Badge>
                ) : (
                  <Badge tone="success">Visible</Badge>
                ),
            },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => navigate(`/display-homes/${row._id}`)}
              onDelete={() => setConfirmDelete(row)}
            />
          )}
          empty={{
            title: "No display homes yet",
            description: "Create your first display home to start populating the public listing.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.name}"?`}
        description="The display home will be removed from the public listing immediately."
        confirmLabel="Delete display home"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}
